import { useCallback, useContext, useState } from 'react';
import { Button } from '@shared/components/Button';
import { Menu } from '@shared/components/Menu';
import { useMenuContext } from '@shared/components/Menu/MenuContext';
import { View } from '@shared/components/View';
import { MBAutosizeTextInput } from '@shared/components/textInput';
import { ColorThemeContext, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import styles from './AuditFieldFooterActions.module.css';

interface Props {
  showFilePicker: () => void;
  uploading: boolean;
  notes: string;
  onNotesChange: (value: string) => void;
  onAnswerPress?: () => void;
}

export function AuditFieldFooterActions({
  showFilePicker,
  uploading,
  notes,
  onNotesChange,
  onAnswerPress,
}: Props) {
  return (
    <View row className={styles.footer} stopPropagation>
      {!!onAnswerPress && (
        <>
          <Button
            variant="ghost"
            size="small"
            icon="pencil"
            label={i18n('card.blocks.question.answerButtonLabel')}
            onClick={onAnswerPress}
          />
        </>
      )}
      <Button
        variant="ghost"
        size="small"
        icon="paperclip"
        label={i18n('card.blocks.question.addFile')}
        onClick={showFilePicker}
        disabled={uploading}
        loading={uploading}
      />
      <Menu align="left">
        <Menu.Trigger>
          <Button
            variant="ghost"
            size="small"
            icon="multi-line"
            label={i18n('card.blocks.question.notes')}
          />
        </Menu.Trigger>
        <Menu.Content style={{ minWidth: grid(35) }}>
          <NotesContent notes={notes} onNotesChange={onNotesChange} />
        </Menu.Content>
      </Menu>
    </View>
  );
}

interface NotesContentProps {
  notes: string;
  onNotesChange: (value: string) => void;
}

function NotesContent({ notes, onNotesChange }: NotesContentProps) {
  const colors = useContext(ColorThemeContext);
  const { close } = useMenuContext();
  const [localNotes, setLocalNotes] = useState(notes);

  const saveNotes = useCallback(() => {
    onNotesChange(localNotes);
    close();
  }, [localNotes, onNotesChange, close]);

  return (
    <View paddingHorizontal={2} paddingVertical={1} gap={2}>
      <MBAutosizeTextInput
        style={{
          backgroundColor: colors.backgroundInactive,
          borderRadius: grid(1),
          minHeight: grid(15),
        }}
        onChangeText={setLocalNotes}
        initialValue={notes}
        placeholder={i18n('card.blocks.question.notesPlaceholder')}
        autoFocus
        padding="normal"
      />
      <Button variant="primary" label={i18n('common.ok')} onClick={saveNotes} />
    </View>
  );
}
